import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

import { exportTransactions, getTransactionsCount } from 'services/repositories/transactions/transactions';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { TransactionFilterKey } from 'support/types/filterKeys';
import { FilterValue } from 'support/types/filters';
import { Popup } from 'components/Display/Popup/Popup';
import { Button } from 'components/Form/Button/Button';

type Props = {
  filterValues: Array<FilterValue>;
};

// Above this many matching transactions we don't export everything synchronously
// (a no-filter pull on a large partner can be 100k+ rows). Instead we offer a
// capped export of the most recent 30 days and nudge the user to narrow filters.
const EXPORT_THRESHOLD = 5_000;
const CAP_DAYS = 30;

export const ExportTransactionsButton = ({ filterValues }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [overLimitCount, setOverLimitCount] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const notifyError = () => {
    addNotification({ title: t('transactions:list.export.error.title') }, NotificationType.error);
  };

  const runExport = async (values: Array<FilterValue>) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);

    try {
      await exportTransactions(values, controller.signal);
    } catch {
      if (!controller.signal.aborted) {
        notifyError();
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setIsLoading(false);
      setOverLimitCount(null);
    }
  };

  const handleClick = async () => {
    if (isLoading) {
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);

    try {
      const count = await getTransactionsCount(filterValues, controller.signal);

      if (count > EXPORT_THRESHOLD) {
        setOverLimitCount(count);
        setIsLoading(false);
        return;
      }

      await exportTransactions(filterValues, controller.signal);
    } catch {
      if (!controller.signal.aborted) {
        notifyError();
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setIsLoading(false);
    }
  };

  const PrimaryButton = () => (
    <Button
      onClick={() => runExport(withRecentDateCap(filterValues))}
      loading={isLoading}
      analyticsId="transactions:export_capped_confirm"
    >
      {t('transactions:list.export.overLimit.confirm', { days: CAP_DAYS })}
    </Button>
  );

  const CancelButton = ({ cancelButtonRef }: { cancelButtonRef: React.RefObject<HTMLButtonElement | null> }) => (
    <Button
      onClick={() => setOverLimitCount(null)}
      disabled={isLoading}
      variant="secondary"
      ref={cancelButtonRef}
      analyticsId="transactions:export_capped_cancel"
    >
      {t('common:cancel')}
    </Button>
  );

  return (
    <>
      <Button
        variant="secondary"
        size="base"
        LeftIcon={ArrowDownTrayIcon}
        loading={isLoading}
        disabled={isLoading}
        onClick={handleClick}
        analyticsId="transactions:export_csv"
        className="cursor-pointer disabled:cursor-not-allowed"
      >
        {t('transactions:list.export.button')}
      </Button>

      <Popup
        open={overLimitCount !== null}
        onClose={() => setOverLimitCount(null)}
        title={t('transactions:list.export.overLimit.title', { days: CAP_DAYS })}
        subtitle={t(
          parseExistingEndDate(filterValues) !== null
            ? 'transactions:list.export.overLimit.subtitleWithDateRange'
            : 'transactions:list.export.overLimit.subtitle',
          { days: CAP_DAYS },
        )}
        PrimaryButton={PrimaryButton}
        CancelButton={CancelButton}
        testId="export-over-limit-modal"
      />
    </>
  );
};

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

const parseExistingEndDate = (filterValues: Array<FilterValue>): Date | null => {
  const existing = filterValues.find((filter) => filter.key === TransactionFilterKey.transactionCreatedBetween);
  if (!existing) {
    return null;
  }

  const parts = existing.value.split(',');
  if (parts.length !== 2) {
    return null;
  }

  const parsed = new Date(parts[1]);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const withRecentDateCap = (filterValues: Array<FilterValue>): Array<FilterValue> => {
  // If the user already has a date range, anchor the cap to the end of that
  // range — otherwise capping to "last 30 days from today" can produce an
  // empty CSV when the selected range is fully in the past.
  const to = parseExistingEndDate(filterValues) ?? new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - CAP_DAYS);

  return [
    ...filterValues.filter((filter) => filter.key !== TransactionFilterKey.transactionCreatedBetween),
    {
      key: TransactionFilterKey.transactionCreatedBetween,
      value: `${toIsoDate(from)},${toIsoDate(to)}`,
    },
  ];
};

/**
 * The overLimit subtitle is selected with a ternary inside t(); enumerate both
 * branches so the i18next-parser keeps them.
 *
 * i18n keys
 * t('transactions:list.export.overLimit.subtitle', { days })
 * t('transactions:list.export.overLimit.subtitleWithDateRange', { days })
 */
